import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

type MousePosition = { x: number; y: number }

type UseFlyoutSafeAreaReturn = {
    /** Ref to attach to the nav item wrapper element */
    wrapperRef: RefObject<HTMLDivElement | null>
    /** Current mouse position in viewport coordinates */
    mousePosition: MousePosition
    /** Whether the safe area should be shown (mouse recently moved within this wrapper) */
    shouldShowSafeArea: boolean
    /** Handler to attach to onMouseMove on the wrapper element */
    handleMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void
    /** Handler to attach to onMouseLeave on the wrapper element */
    handleMouseLeave: () => void
}

/**
 * Hook to track mouse position for flyout safe area calculation.
 *
 * When a user hovers over a nav item with a flyout, they often move their mouse
 * diagonally toward the flyout. Without a "safe area", this diagonal movement
 * would cross over other nav items, triggering their hover states and closing
 * the original flyout.
 *
 * This hook tracks mouse position and provides the data needed to render a
 * triangular safe area that keeps the flyout open during diagonal movement.
 *
 * The safe area is shown when the mouse has moved within this wrapper in the
 * last 175ms. We only use mousemove (not mouseenter) since receiving a mousemove
 * implies the mouse is in the wrapper.
 */
export const useFlyoutSafeArea = (): UseFlyoutSafeAreaReturn => {
    const wrapperRef = useRef<HTMLDivElement>(null)
    const [mouseIsActive, setMouseIsActive] = useState(false)
    const [mousePosition, setMousePosition] = useState<MousePosition>({ x: 0, y: 0 })
    const timerRef = useRef<number | null>(null)

    const startInactivityTimer = useCallback((): void => {
        if (timerRef.current) {
            window.clearTimeout(timerRef.current)
        }

        timerRef.current = window.setTimeout(() => {
            setMouseIsActive(false)
            timerRef.current = null
        }, 175)
    }, [])

    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>): void => {
        if (!mouseIsActive) {
            setMouseIsActive(true)
        }

        startInactivityTimer()
        setMousePosition({ x: e.clientX, y: e.clientY })
    }, [mouseIsActive, startInactivityTimer])

    const handleMouseLeave = useCallback((): void => {
        setMouseIsActive(false)
        if (timerRef.current) {
            window.clearTimeout(timerRef.current)
            timerRef.current = null
        }
    }, [])

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                window.clearTimeout(timerRef.current)
            }
        }
    }, [])

    return {
        wrapperRef,
        mousePosition,
        shouldShowSafeArea: mouseIsActive,
        handleMouseMove,
        handleMouseLeave,
    }
}
